import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import {
  PageHeader,
  PageContent,
  EmptyState,
  LoadingState,
  Button,
  Table,
  Card,
} from '@printtat/ui';
import type { Column } from '@printtat/ui';
import { adminApi } from '@/services/adminApi';
import type {
  AwaitingReconciliationRow,
  ReconciliationStats,
  ReconciliationEventRow,
} from '@/services/adminApi';
import { queryKeys } from '@/lib/queryKeys';

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatAge = (minutes: number) => {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

const OUTCOME_BADGE: Record<string, string> = {
  reconciliation_attempt: 'bg-gray-50 text-gray-600',
  reconciliation_recovered: 'bg-green-50 text-green-700',
  reconciliation_no_match: 'bg-yellow-50 text-yellow-700',
  reconciliation_lookup_failed: 'bg-red-50 text-red-700',
  payment_recovered: 'bg-green-50 text-green-700',
};

export const AdminPaymentReconciliationPage = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data: stats } = useQuery<ReconciliationStats>({
    queryKey: queryKeys.paymentReconciliation.stats(),
    queryFn: () => adminApi.paymentReconciliation.stats(),
    refetchInterval: 5000,
  });

  const { data, isLoading } = useQuery<{
    rows: AwaitingReconciliationRow[];
    total: number;
  }>({
    queryKey: [...queryKeys.paymentReconciliation.all(), { page, limit }],
    queryFn: () => adminApi.paymentReconciliation.list({ page, limit }),
    refetchInterval: 10000,
  });

  const { data: eventsData } = useQuery<{
    rows: ReconciliationEventRow[];
    total: number;
  }>({
    queryKey: queryKeys.paymentReconciliation.events(),
    queryFn: () => adminApi.paymentReconciliation.events({ page: 1, limit: 100 }),
    refetchInterval: 10000,
  });

  const reconcileMutation = useMutation({
    mutationFn: (orderId: string) => adminApi.paymentReconciliation.reconcile(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.paymentReconciliation.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.paymentReconciliation.stats() });
      queryClient.invalidateQueries({ queryKey: queryKeys.paymentReconciliation.events() });
    },
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const columns: Column<AwaitingReconciliationRow>[] = [
    {
      key: 'orderNumber',
      header: 'Order',
      render: (r) => (
        <a
          href={`/orders/${r.orderId}`}
          className="text-sm font-mono text-blue-600 hover:underline"
        >
          {r.orderNumber}
        </a>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (r) => <span className="text-sm">{r.totalAmount} EGP</span>,
    },
    {
      key: 'age',
      header: 'Age',
      render: (r) => (
        <span
          className={`text-sm ${r.ageMinutes > 120 ? 'text-red-600 font-medium' : r.ageMinutes > 5 ? 'text-yellow-700' : 'text-gray-500'}`}
        >
          {formatAge(r.ageMinutes)}
        </span>
      ),
    },
    {
      key: 'pendingPayments',
      header: 'Pending Attempts',
      render: (r) => <span className="text-sm text-gray-600">{r.pendingPaymentCount}</span>,
    },
    {
      key: 'lastAttempt',
      header: 'Last Reconciliation',
      render: (r) =>
        r.lastReconciliationAttempt ? (
          <span className="text-sm text-gray-500">{formatDate(r.lastReconciliationAttempt)}</span>
        ) : (
          <span className="text-xs text-gray-400">never</span>
        ),
    },
    {
      key: 'lastOutcome',
      header: 'Last Outcome',
      render: (r) =>
        r.lastReconciliationOutcome ? (
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${OUTCOME_BADGE[r.lastReconciliationOutcome] ?? 'bg-gray-50 text-gray-600'}`}
          >
            {r.lastReconciliationOutcome.replace('reconciliation_', '')}
          </span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <Button
          size="sm"
          variant="outline"
          onClick={() => reconcileMutation.mutate(r.orderId)}
          disabled={reconcileMutation.isPending}
        >
          <RefreshCw
            className={`w-3.5 h-3.5 mr-1 ${reconcileMutation.isPending ? 'animate-spin' : ''}`}
          />
          Reconcile now
        </Button>
      ),
    },
  ];

  return (
    <PageContent>
      <PageHeader
        title="Payment Reconciliation"
        description="Orders awaiting reconciliation with Paymob. Use 'Reconcile now' to manually check Paymob for a successful transaction."
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Awaiting &gt; 5 min</p>
              <p className="text-2xl font-semibold">{stats?.awaitingOver5Min ?? '—'}</p>
            </div>
            <Clock className="w-6 h-6 text-yellow-500" />
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Recovered (24h)</p>
              <p className="text-2xl font-semibold">{stats?.recoveredLast24h ?? '—'}</p>
            </div>
            <CheckCircle className="w-6 h-6 text-green-500" />
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Attempts (24h)</p>
              <p className="text-2xl font-semibold">{stats?.attemptsLast24h ?? '—'}</p>
            </div>
            <RefreshCw className="w-6 h-6 text-blue-500" />
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Failed at 2h (24h)</p>
              <p className="text-2xl font-semibold">{stats?.unreconciledFailedLast24h ?? '—'}</p>
            </div>
            <AlertCircle className="w-6 h-6 text-red-500" />
          </div>
        </Card>
      </div>

      <h2 className="text-lg font-medium mb-3">Awaiting reconciliation ({total})</h2>
      {isLoading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nothing to reconcile"
          description="All orders are either paid or already failed. Reconciliation runs automatically every 30 minutes for orders older than 2 hours, and once daily for the long tail."
        />
      ) : (
        <>
          <Table data={rows} columns={columns} keyExtractor={(r) => r.orderId} />
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
              <span>
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <h2 className="text-lg font-medium mt-8 mb-3">Recent activity</h2>
      <div className="border rounded-lg bg-white divide-y">
        {(eventsData?.rows ?? []).slice(0, 50).map((ev) => (
          <div key={ev.id} className="px-4 py-2 flex items-center justify-between text-sm">
            <div className="flex items-center gap-3">
              <span
                className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${OUTCOME_BADGE[ev.type] ?? 'bg-gray-50 text-gray-600'}`}
              >
                {ev.type.replace(/_/g, ' ')}
              </span>
              {ev.orderId && (
                <a
                  href={`/orders/${ev.orderId}`}
                  className="font-mono text-xs text-blue-600 hover:underline"
                >
                  {ev.orderId.slice(0, 8)}…
                </a>
              )}
              {ev.data?.source ? (
                <span className="text-xs text-gray-500">via {String(ev.data.source)}</span>
              ) : null}
            </div>
            <span className="text-xs text-gray-400">{formatDate(ev.createdAt)}</span>
          </div>
        ))}
        {(eventsData?.rows ?? []).length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-gray-400">
            No reconciliation activity yet
          </div>
        )}
      </div>
    </PageContent>
  );
};
